import React, { useCallback, useEffect, useState } from 'react';
import { api, federation } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';
import { track, FINDING_ACCEPTED } from '../../utils/analytics';
import FederationGate from './FederationGate';
import {
  ErrorBanner,
  FINDING_STATUSES,
  LoadingBlock,
  PageHeader,
  btnPrimary,
  btnSecondary,
  canWrite,
  inputClass,
  safeText,
} from './federationShared';

function FindingsInner() {
  const { user } = useAuth();
  const writable = canWrite(user);

  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [dismissId, setDismissId] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [reviewId, setReviewId] = useState(null);
  const [reviewDate, setReviewDate] = useState('');
  const [teams, setTeams] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ teamId: '', programId: '', submissionId: '', findingType: '', findingText: '', evidenceSummary: '' });

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (statusFilter) params.status = statusFilter;
    federation
      .getFindings(params)
      .then((res) => setFindings(res.findings || []))
      .catch((e) => {
        setFindings([]);
        setError(e.message || 'Could not load findings');
      })
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    Promise.all([
      api.getTeams().catch(() => ({ teams: [] })),
      federation.getPrograms().catch(() => ({ programs: [] })),
      federation.getSubmissions().catch(() => ({ submissions: [] })),
    ]).then(([t, p, s]) => {
      setTeams(t.teams || []);
      setPrograms(p.programs || []);
      setSubmissions(s.submissions || []);
    });
  }, []);

  const createFinding = async (event) => {
    event.preventDefault();
    if (!form.teamId || !form.findingText.trim()) {
      setError('Team and finding are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await federation.createFinding({
        ...form,
        sourceType: form.submissionId ? 'program_submission' : 'manual',
        findingText: form.findingText.trim(),
        evidenceSummary: form.evidenceSummary.trim() || undefined,
      });
      setForm({ teamId: '', programId: '', submissionId: '', findingType: '', findingText: '', evidenceSummary: '' });
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.message || 'Could not record finding');
    } finally {
      setSaving(false);
    }
  };

  const patchFinding = async (payload) => {
    setBusyId(payload.id);
    setError('');
    try {
      await federation.updateFinding(payload);
      if (payload.accept) track(FINDING_ACCEPTED);
      setDismissId(null);
      setDismissReason('');
      setReviewId(null);
      setReviewDate('');
      load();
    } catch (e) {
      setError(e.message || 'Could not update finding');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Findings"
        description="Organization priorities recorded from program submissions, leadership review, imports, or optional integrations."
        actions={writable ? (
          <button type="button" className={btnPrimary} onClick={() => setShowForm((value) => !value)}>
            {showForm ? 'Cancel' : 'Record finding'}
          </button>
        ) : null}
      />

      {showForm && writable && (
        <form onSubmit={createFinding} className="border border-navy-200 rounded-xl bg-white p-5 mb-6 space-y-4 max-w-xl">
          <h2 className="text-base font-semibold text-navy-900">New organizational finding</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Team</label>
              <select className={inputClass} value={form.teamId} onChange={(e) => setForm((f) => ({ ...f, teamId: e.target.value }))}>
                <option value="">Select team</option>
                {teams.map((team) => <option key={team.id} value={team.id}>{safeText(team.name)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-900 mb-1">Program</label>
              <select className={inputClass} value={form.programId} onChange={(e) => setForm((f) => ({ ...f, programId: e.target.value }))}>
                <option value="">Optional</option>
                {programs.map((program) => <option key={program.id} value={program.id}>{safeText(program.name)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Program submission</label>
            <select className={inputClass} value={form.submissionId} onChange={(e) => {
              const selected = submissions.find((submission) => submission.id === e.target.value);
              setForm((f) => ({
                ...f,
                submissionId: e.target.value,
                programId: selected?.programId || f.programId,
                teamId: selected?.teamId || f.teamId,
              }));
            }}>
              <option value="">Optional source submission</option>
              {submissions.map((submission) => <option key={submission.id} value={submission.id}>{safeText(submission.title)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Priority type</label>
            <input className={inputClass} value={form.findingType} onChange={(e) => setForm((f) => ({ ...f, findingType: e.target.value }))} maxLength={160} />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Finding</label>
            <textarea className={inputClass} rows={3} value={form.findingText} onChange={(e) => setForm((f) => ({ ...f, findingText: e.target.value }))} maxLength={4000} />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-900 mb-1">Evidence summary</label>
            <textarea className={inputClass} rows={2} value={form.evidenceSummary} onChange={(e) => setForm((f) => ({ ...f, evidenceSummary: e.target.value }))} maxLength={4000} />
          </div>
          <button type="submit" className={btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Record finding'}</button>
        </form>
      )}

      <div className="mb-6 max-w-xs">
        <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
        <select
          className={inputClass}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {FINDING_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <ErrorBanner message={error} onRetry={load} />

      {loading && <LoadingBlock label="Loading findings…" />}

      {!loading && findings.length === 0 && !error && (
        <div className="border border-navy-200 rounded-xl bg-white px-6 py-10 text-center">
          <p className="text-navy-900 font-medium">No findings</p>
          <p className="text-sm text-gray-500 mt-2">
            Record a priority directly or create one from a program submission or optional integration.
          </p>
        </div>
      )}

      {!loading && findings.length > 0 && (
        <ul className="space-y-3">
          {findings.map((finding) => (
            <li key={finding.id} className="border border-navy-200 rounded-xl bg-white px-4 py-4">
              <p className="text-sm text-navy-900 whitespace-pre-wrap">{safeText(finding.findingText)}</p>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                <span>Status: {safeText(finding.status)}</span>
                <span>Source: {safeText(finding.sourceType, 'manual')}</span>
                <span>Review: {safeText(finding.reviewStatus)}</span>
                {finding.targetReviewDate ? (
                  <span>Target review: {safeText(finding.targetReviewDate)}</span>
                ) : null}
                <span>{safeText(finding.createdAt)}</span>
              </div>
              {finding.dismissedReason ? (
                <p className="text-xs text-gray-500 mt-2">Dismissed: {safeText(finding.dismissedReason)}</p>
              ) : null}

              {writable && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {finding.status !== 'Accepted' && finding.status !== 'Dismissed' && (
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={busyId === finding.id}
                      onClick={() => patchFinding({ id: finding.id, accept: true })}
                    >
                      Accept
                    </button>
                  )}
                  {finding.status !== 'Dismissed' && (
                    <button
                      type="button"
                      className={btnSecondary}
                      disabled={busyId === finding.id}
                      onClick={() => {
                        setDismissId(finding.id);
                        setDismissReason('');
                        setReviewId(null);
                      }}
                    >
                      Dismiss
                    </button>
                  )}
                  <button
                    type="button"
                    className={btnSecondary}
                    disabled={busyId === finding.id}
                    onClick={() => {
                      setReviewId(finding.id);
                      setReviewDate(finding.targetReviewDate || '');
                      setDismissId(null);
                    }}
                  >
                    Set review date
                  </button>
                </div>
              )}

              {dismissId === finding.id && (
                <div className="mt-3 border border-navy-100 rounded-lg p-3 space-y-2 max-w-lg">
                  <label className="block text-xs font-medium text-gray-500">Dismiss reason</label>
                  <textarea
                    className={inputClass}
                    rows={2}
                    value={dismissReason}
                    onChange={(e) => setDismissReason(e.target.value)}
                    maxLength={2000}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={busyId === finding.id}
                      onClick={() =>
                        patchFinding({
                          id: finding.id,
                          dismiss: true,
                          dismissedReason: dismissReason.trim() || undefined,
                        })
                      }
                    >
                      Confirm dismiss
                    </button>
                    <button type="button" className={btnSecondary} onClick={() => setDismissId(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {reviewId === finding.id && (
                <div className="mt-3 border border-navy-100 rounded-lg p-3 space-y-2 max-w-sm">
                  <label className="block text-xs font-medium text-gray-500">Target review date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={reviewDate}
                    onChange={(e) => setReviewDate(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={busyId === finding.id || !reviewDate}
                      onClick={() =>
                        patchFinding({ id: finding.id, targetReviewDate: reviewDate })
                      }
                    >
                      Save date
                    </button>
                    <button type="button" className={btnSecondary} onClick={() => setReviewId(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function FederationFindings() {
  return (
    <FederationGate>
      <FindingsInner />
    </FederationGate>
  );
}
